import React, { Component } from 'react';
import {
  withStyles,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Paper,
} from '@material-ui/core';
import { RouteComponentProps } from 'react-router-dom';
import Permissions from '../Permissions/Permissions.container';
import EditRoleActionButtons from '../ActionButtons/EditRoleActionButtons';
import Loader from '../../../shared/Loaders/Loader';
import RolePageContainer from '../RolePageContainer';

const styles = (theme) => ({
  container: {
    minHeight: 'calc(100vh - 80px)',
    boxSizing: 'border-box',
    padding: '40px 0px',
    maxWidth: '990px',
    width: '100%',
    margin: '0px auto',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: theme.spacing(4),
    backgroundColor: '#fafafa',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
  },
});

interface EditProps extends RouteComponentProps<{ roleId: string }> {
  updateRole: (roleId: string, data: any) => Promise<any>;
  fetchRole: (roleId: string) => Promise<any>;
  showToast: (message: string) => void;
  classes: any;
}

class Edit extends Component<EditProps> {
  state = {
    title: '',
    description: '',
    permissions: [],
    loading: false,
    fallbackRole: 'educator',
    roleId: null,
  };

  fetchRoleData = async (roleId: string) => {
    this.setState({ roleId, loading: true });
    try {
      const roleData = await this.props.fetchRole(roleId);
      if (roleData) {
        // Ensure permissions is an array
        // Permissions array now contains permission keys (e.g., "material:create") instead of IDs
        const permissions = Array.isArray(roleData.permissions)
          ? roleData.permissions
          : [];

        this.setState({
          title: roleData.title || roleData.name || '',
          description: roleData.description || '',
          permissions: permissions, // Array of permission keys
          fallbackRole: roleData.fallbackRole || 'educator',
          loading: false,
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error fetching role data:', error);
      this.setState({ loading: false });
      this.props.showToast('Failed to load role details');
    }
  };

  componentDidMount = async () => {
    const { match } = this.props;
    const roleId = match.params.roleId;

    if (roleId) {
      await this.fetchRoleData(roleId);
    }
  };

  componentDidUpdate = async (prevProps: EditProps) => {
    const { match } = this.props;
    const roleId = match.params.roleId;
    const prevRoleId = prevProps.match.params.roleId;

    if (roleId && roleId !== prevRoleId) {
      await this.fetchRoleData(roleId);
    }
  };

  handleTitleChange = (event) => {
    this.setState((prevState) => ({
      ...prevState,
      title: event.target.value,
    }));
  };

  handleDescriptionChange = (event) => {
    this.setState((prevState) => ({
      ...prevState,
      description: event.target.value,
    }));
  };

  handlePermissionsSelection = (permissionId) => {
    const VIEW_PERMISSION = 'material:view';
    const EDUCATOR_AREA_PERMISSION = 'material:educatorArea';

    let temp = [...this.state.permissions];
    const permissionIndex = temp.indexOf(permissionId);

    if (permissionIndex === -1) {
      // Adding permission
      temp.push(permissionId);

      // If any permission other than educator area is selected, ensure view is selected
      if (
        permissionId !== EDUCATOR_AREA_PERMISSION &&
        !temp.includes(VIEW_PERMISSION)
      ) {
        temp.push(VIEW_PERMISSION);
      }
    } else {
      // Removing permission
      temp.splice(permissionIndex, 1);

      // If view is removed, remove all permissions except educator area
      if (permissionId === VIEW_PERMISSION) {
        temp = temp.filter((p) => p === EDUCATOR_AREA_PERMISSION);
      }
    }
    this.setState((prevState) => ({
      ...prevState,
      permissions: temp,
    }));
  };

  getCleanData = () => {
    return {
      name: this.state.title.trim(),
      description: this.state.description.trim(),
      permissions: [...this.state.permissions], // Array of permission keys (e.g., "material:create")
      fallbackRole: this.state.fallbackRole,
    };
  };

  handleSubmit = async () => {
    const cleanData = this.getCleanData();

    if (cleanData.name === '') {
      this.props.showToast('Role name is required');
    } else if (cleanData.description === '') {
      this.props.showToast('Description is required');
    } else if (cleanData.permissions.length === 0) {
      this.props.showToast('At least one permission is required');
    } else {
      if (this.state.roleId) {
        await this.props.updateRole(this.state.roleId, cleanData);
        this.props.history.push('/roles');
      }
    }
  };

  render() {
    const { classes } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <Loader />;
    }

    return (
      <RolePageContainer title={'Edit Role'}>
        <TextField
          label="Role"
          autoFocus
          placeholder="Enter role name"
          variant="outlined"
          inputProps={{
            maxLength: 100,
          }}
          className={classes.textField}
          InputLabelProps={{
            shrink: true,
          }}
          value={this.state.title}
          onChange={this.handleTitleChange}
        />

        <TextField
          label="Description"
          placeholder="Enter role description"
          variant="outlined"
          multiline
          inputProps={{
            maxLength: 400,
          }}
          rows={1}
          className={classes.textField}
          InputLabelProps={{
            shrink: true,
          }}
          value={this.state.description}
          onChange={this.handleDescriptionChange}
        />
        <Paper style={{ padding: '16px' }}>
          <Typography variant="h6">Fallback Role</Typography>
          <Typography variant="body2" color="textSecondary">
            Roles and Permissions are currently available for Materials. For
            other areas (Assignments, Announcements, Common Room, Messages,
            etc.) that are not yet supported, the role will inherit the selected
            fallback role&apos;s permissions.
          </Typography>
          <RadioGroup
            name="role"
            className={classes.radioGroup}
            value={this.state.fallbackRole ?? 'educator'}
            onChange={(e) => {
              const newRole = e.target.value;
              this.setState((prevState: any) => {
                let newPermissions = [...prevState.permissions];
                if (newRole === 'student') {
                  newPermissions = newPermissions.filter(
                    (p) => p !== 'material:educatorArea',
                  );
                }
                if (newRole === 'educator') {
                  newPermissions = newPermissions.filter(
                    (p) => p !== 'material:progress',
                  );
                }
                return {
                  ...prevState,
                  fallbackRole: newRole,
                  permissions: newPermissions,
                };
              });
            }}
          >
            <FormControlLabel
              checked={this.state.fallbackRole === 'educator'}
              value="educator"
              control={<Radio style={{ color: '#452188' }} />}
              label="Educator"
            />
            <FormControlLabel
              checked={this.state.fallbackRole === 'student'}
              value="student"
              control={<Radio style={{ color: '#452188' }} />}
              label="Student"
            />
          </RadioGroup>
        </Paper>
        <Permissions
          selectedValues={this.state.permissions}
          handleChange={this.handlePermissionsSelection}
          fallbackRole={this.state.fallbackRole}
          disabledPermissions={
            this.state.fallbackRole === 'student'
              ? ['material:educatorArea']
              : this.state.fallbackRole === 'educator'
                ? ['material:progress']
                : []
          }
        />
        <EditRoleActionButtons handleSubmit={this.handleSubmit} />
      </RolePageContainer>
    );
  }
}

export default withStyles(styles as any, { withTheme: true })(Edit);
